import { DEFAULT_BLISTER_SETTINGS, normalizeBlisterSettings } from "./blister-settings";
import { DEFAULT_FRACTAL_SETTINGS, normalizeFractalSettings } from "./fractal-settings";
import { DEFAULT_KALEIDOSCOPE_SETTINGS, normalizeKaleidoscopeSettings } from "./kaleidoscope-settings";
import type { ViewMode } from "./shareable-settings";
import type { FractalFamily, FractalSettings } from "../types/fractal";
import type { KaleidoscopeSettings } from "../types/kaleidoscope";
import type { BlisterSettings } from "../types/settings";

export type AppPreset = {
  id: string;
  mode: ViewMode;
  name: string;
  description: string;
  colors: string[];
  settings: BlisterSettings | KaleidoscopeSettings | FractalSettings;
};

export const APP_PRESETS: AppPreset[] = [
  {
    id: "glitter-soft-focus",
    mode: "glitter",
    name: "Soft Focus",
    description: "Slow glowing depth with a calm center.",
    colors: ["#f7d6ff", "#c084fc", "#7c3aed", "#4f46e5", "#2563eb", "#07111f"],
    settings: normalizeBlisterSettings({
      ...DEFAULT_BLISTER_SETTINGS,
      colors: ["#f7d6ff", "#c084fc", "#7c3aed", "#4f46e5", "#2563eb", "#07111f"],
      particleSize: 0.62,
      speed: 0.045,
      count: 2100,
      opacity: 0.78,
      glowStrength: 8.8,
      blurStrength: 8.2,
      atmosphereIntensity: 0.72,
      hazeAmount: 0.74,
      sparkleCount: 520,
      pulseIntensity: 0.08,
      bloomIntensity: 0.84,
      vignetteAmount: 0.42,
    }),
  },
  {
    id: "glitter-night-drive",
    mode: "glitter",
    name: "Night Drive",
    description: "Bright particles drifting through dark glass.",
    colors: ["#bde7ff", "#68d8ff", "#3b82f6", "#7c3aed", "#f472b6", "#020617"],
    settings: normalizeBlisterSettings({
      ...DEFAULT_BLISTER_SETTINGS,
      colors: ["#bde7ff", "#68d8ff", "#3b82f6", "#7c3aed", "#f472b6", "#020617"],
      particleSize: 0.36,
      speed: 0.105,
      count: 3200,
      opacity: 0.7,
      speedRandomness: 1.25,
      glowStrength: 9.5,
      blurStrength: 4.4,
      atmosphereIntensity: 0.5,
      sparkleCount: 1300,
      pulseIntensity: 0.18,
      pulseFrequency: 0.28,
      bloomIntensity: 1.06,
      chromaticAberration: 0.1,
    }),
  },
  {
    id: "kaleidoscope-prism-tunnel",
    mode: "kaleidoscope",
    name: "Prism Tunnel",
    description: "Clean mirrored depth with readable glass bands.",
    colors: ["#f8fbff", "#bfe8ff", "#79c6ff", "#566dff", "#1f2d68", "#030612"],
    settings: normalizeKaleidoscopeSettings({
      ...DEFAULT_KALEIDOSCOPE_SETTINGS,
      colors: ["#f8fbff", "#bfe8ff", "#79c6ff", "#566dff", "#1f2d68", "#030612"],
      segments: 12,
      facetDensity: 0.54,
      patternComplexity: 0.48,
      centerStructure: 0.9,
      outerReflection: 0.82,
      layerDepth: 0.9,
      rotationSpeed: 0.1,
      morphSpeed: 0.14,
      shimmerSpeed: 0.28,
      pulseStrength: 0.1,
      glowStrength: 0.62,
      bloomRadius: 0.24,
      highlightIntensity: 0.96,
      refractionStrength: 0.34,
      contrast: 0.92,
      sparkleDensity: 0.14,
      microFacetStrength: 0.42,
      textureAmount: 0.22,
      paletteIntensity: 1.08,
      colorSeparation: 0.72,
      darkLightBalance: 0.36,
    }),
  },
  {
    id: "kaleidoscope-warm-glass",
    mode: "kaleidoscope",
    name: "Warm Glass",
    description: "Jewelry-box symmetry with soft amber reflections.",
    colors: ["#fff4d6", "#ffc66d", "#f08a74", "#a66cff", "#3441a4", "#05040a"],
    settings: normalizeKaleidoscopeSettings({
      ...DEFAULT_KALEIDOSCOPE_SETTINGS,
      colors: ["#fff4d6", "#ffc66d", "#f08a74", "#a66cff", "#3441a4", "#05040a"],
      segments: 10,
      facetDensity: 0.68,
      patternComplexity: 0.58,
      centerStructure: 0.78,
      outerReflection: 0.74,
      layerDepth: 0.72,
      rotationSpeed: 0.08,
      morphSpeed: 0.18,
      shimmerSpeed: 0.34,
      pulseStrength: 0.12,
      glowStrength: 0.74,
      bloomRadius: 0.3,
      highlightIntensity: 0.88,
      refractionStrength: 0.24,
      contrast: 0.78,
      sparkleDensity: 0.2,
      microFacetStrength: 0.5,
      textureAmount: 0.32,
      paletteIntensity: 1.02,
      colorSeparation: 0.62,
      darkLightBalance: 0.42,
    }),
  },
  {
    id: "fractal-infinite-mandelbrot",
    mode: "fractal",
    name: "Infinite Mandelbrot",
    description: "Self-similar Mandelbrot zoom with hidden loop handoff.",
    colors: ["#f5fbff", "#9ce9ff", "#31a7ff", "#072777", "#02082c", "#000000"],
    settings: normalizeFractalSettings({
      ...DEFAULT_FRACTAL_SETTINGS,
      fractalFamily: "monolith",
      fractalVariant: "infiniteZoom",
      colors: ["#f5fbff", "#9ce9ff", "#31a7ff", "#072777", "#02082c", "#000000"],
      intensity: 0.78,
      brightness: 0.46,
      contrast: 0.66,
      scale: 0.5,
      glow: 0.2,
      motionSpeed: 0.18,
      paletteIntensity: 0.88,
      saturation: 0.92,
      grain: 0.01,
    }),
  },
  {
    id: "fractal-flarebrot",
    mode: "fractal",
    name: "FlareBrot",
    description: "Loopable noisy flare field driven by Mandelbrot distance.",
    colors: ["#fff0ee", "#ff7a81", "#ff1a33", "#761222", "#170407", "#000000"],
    settings: normalizeFractalSettings({
      ...DEFAULT_FRACTAL_SETTINGS,
      fractalFamily: "mandala",
      fractalVariant: "flarebrot",
      colors: ["#fff0ee", "#ff7a81", "#ff1a33", "#761222", "#170407", "#000000"],
      intensity: 0.78,
      brightness: 0.29,
      contrast: 0.4,
      scale: 0.4,
      symmetry: 0.24,
      glow: 0,
      motionSpeed: 0.3,
      fractalDepth: 0.72,
      repetition: 0.16,
      detail: 0.52,
      paletteIntensity: 1,
      saturation: 0.9,
    }),
  },
  {
    id: "fractal-voxel-tunnel",
    mode: "fractal",
    name: "Voxel Tunnel",
    description: "Procedural grid tunnel with metallic light cells.",
    colors: ["#e9fbff", "#8ee8ff", "#44a4ff", "#2743a2", "#0a1026", "#020208"],
    settings: normalizeFractalSettings({
      ...DEFAULT_FRACTAL_SETTINGS,
      fractalFamily: "crystal",
      fractalVariant: "voxelTunnel",
      colors: ["#e9fbff", "#8ee8ff", "#44a4ff", "#2743a2", "#0a1026", "#020208"],
      intensity: 0.82,
      brightness: 0.54,
      contrast: 0.78,
      scale: 0.34,
      glow: 0.42,
      motionSpeed: 0.58,
      repetition: 0.22,
      fieldWarp: 0.35,
      detail: 0.72,
      paletteIntensity: 0.9,
      saturation: 0.86,
    }),
  },
  {
    id: "fractal-planar-light",
    mode: "fractal",
    name: "Planar Light",
    description: "Planar light bands rushing through a spectral tunnel.",
    colors: ["#f1f7ff", "#84e5ff", "#9777ff", "#ff4aa2", "#1b0d3e", "#030306"],
    settings: normalizeFractalSettings({
      ...DEFAULT_FRACTAL_SETTINGS,
      fractalFamily: "nebula",
      fractalVariant: "planarLight",
      colors: ["#f1f7ff", "#84e5ff", "#9777ff", "#ff4aa2", "#1b0d3e", "#030306"],
      intensity: 0.82,
      brightness: 0.54,
      contrast: 0.66,
      scale: 0.48,
      glow: 0.38,
      motionSpeed: 0.42,
      repetition: 0.5,
      fieldWarp: 0.36,
      detail: 0.72,
      paletteIntensity: 0.95,
      saturation: 0.88,
    }),
  },
];

export function getPresetsForMode(mode: ViewMode) {
  return APP_PRESETS.filter((preset) => preset.mode === mode);
}

export function getFractalPresetForFamily(family: FractalFamily) {
  return APP_PRESETS.find(
    (preset): preset is AppPreset & { settings: FractalSettings } =>
      preset.mode === "fractal" && (preset.settings as FractalSettings).fractalFamily === family
  ) ?? APP_PRESETS.find(
    (preset): preset is AppPreset & { settings: FractalSettings } => preset.mode === "fractal"
  );
}

export function getFractalPresetSettingsForFamily(family: FractalFamily) {
  const preset = getFractalPresetForFamily(family);
  return preset ? normalizeFractalSettings(preset.settings) : DEFAULT_FRACTAL_SETTINGS;
}
